import {Box, Button, ButtonGroup, Table, Tabs, Text} from "@chakra-ui/react";
import {Form, UI} from "components";
import Moment from "moment";
import {useState, useEffect, useMemo} from "react";
import useReportBuyCustomersMost from "hooks/useReportBuyCustomersMost";
import useExportReportType from "hooks/useExportReportType";

export default function AnalyticsClient() {
    const initialPeriod = [
        Moment().startOf("month").toDate(),
        Moment().endOf("month").toDate()
    ];

    const [period, setPeriod] = useState(initialPeriod)
    const [activeTab, setActiveTab] = useState(null)

    const validPeriod =
        period && period[0] instanceof Date && period[1] instanceof Date
            ? [Moment(period[0]).format('YYYY-MM-DD'), Moment(period[1]).format('YYYY-MM-DD')]
            : null

    const startDate = validPeriod ? validPeriod[0] : null
    const endDate = validPeriod ? validPeriod[1] : null

    const report = useReportBuyCustomersMost(startDate, endDate)
    const exportReport = useExportReportType()

    const hasMultipleMonths = report.data?.months && typeof report.data.months === 'object'
    const monthsList = hasMultipleMonths ? Object.keys(report.data.months) : []

    useEffect(() => {
        if (hasMultipleMonths && !activeTab && monthsList.length > 0) {
            setActiveTab(monthsList[0])
        }
    }, [hasMultipleMonths, activeTab, monthsList])

    const customersData = useMemo(() => {
        if (!report.data) return []

        if (hasMultipleMonths) {
            return []
        }

        if (Array.isArray(report.data)) {
            return report.data
        }

        if (report.data.customers) {
            return report.data.customers
        }

        return []
    }, [report.data, hasMultipleMonths])

    const hasNoData = useMemo(() => {
        if (report.isFetching) return false

        if (!report.data) return true

        if (hasMultipleMonths) {
            return monthsList.length === 0 || monthsList.every(month => {
                const customers = report.data.months?.[month]
                return !customers || customers.length === 0
            })
        }

        return !customersData || customersData.length === 0
    }, [report.data, report.isFetching, hasMultipleMonths, monthsList, customersData])

    const columns = [
        {children: 'Cliente'},
        {children: 'E-mail'},
        {children: 'Celular'},
        {children: 'Total em compras', textAlign: 'right'},
    ]

    const handleResetFilters = () => {
        setPeriod(initialPeriod)
        setActiveTab(null)
    }

    const handleExportReportType = (reportType, exportMine) => {
        if (startDate && endDate) {
            exportReport.mutate({
                reportType,
                exportMine,
                startDate,
                endDate,
            })
        }
    }

    const handleExportCsv = () => {
        if (!report.data) return;

        const formatCurrency = (value) => {
            const numberValue = value / 100;
            return numberValue.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            });
        };

        const formatMobile = (mobile) => {
            if (!mobile) return "";
            return `="${mobile}"`;
        };

        let csvContent = "";

        if (hasMultipleMonths && report.data?.months) {
            for (const month of monthsList) {
                const customers = report.data.months[month] || [];
                if (customers.length === 0) continue;

                csvContent += `"${month}"\n`;
                const header = ["Cliente", "E-mail", "Celular", "Total em compras"];
                csvContent += header.join(";") + "\n";

                customers.forEach((customer) => {
                    const row = [
                        customer.customer_name || "",
                        customer.email || "",
                        formatMobile(customer.mobile || ""),
                        formatCurrency(customer.total || 0)
                    ].join(";");
                    csvContent += row + "\n";
                });
                csvContent += "\n";
            }
        } else {
            const customers = customersData;
            if (customers.length === 0) return;

            const header = ["Cliente", "E-mail", "Celular", "Total em compras"];
            csvContent = [
                header.join(";"),
                ...customers.map((customer) => [
                    customer.customer_name || "",
                    customer.email || "",
                    formatMobile(customer.mobile || ""),
                    formatCurrency(customer.total || 0)
                ].join(";"))
            ].join("\n");
        }

        const blob = new Blob(["\uFEFF" + csvContent], {type: "text/csv;charset=utf-8;"});
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `relatorio-clientes-que-mais-compram-${Moment(startDate).format('DD-MM-YYYY')}-a-${Moment(endDate).format('DD-MM-YYYY')}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const isValidWhatsAppNumber = (number) => {
        if (!number) return false
        const cleanNumber = number.toString().replace(/\D/g, '')
        return cleanNumber.length >= 10 && cleanNumber.length <= 13
    }

    const formatWhatsAppLink = (number) => {
        if (!number) return '#'
        const cleanNumber = number.toString().replace(/\D/g, '')
        return isValidWhatsAppNumber(number) ? `https://wa.me/${cleanNumber}` : '#'
    }

    return (
        <>
            <UI.Header title="Clientes que Mais Compram"/>

            <Box mb={5}>
                <UI.Alert title="Atenção sobre filtro">Ao selecionar um período com mais de um mês, os dados serão
                    exibidos
                    separadamente por mês, utilizando abas. Para visualizar o ranking geral consolidado de clientes,
                    selecione um período de até 30 dias.
                </UI.Alert>
            </Box>

            <UI.Table
                loading={report.isFetching}
                loadingText="Buscando clientes..."
                label="Ranking de clientes:"
                columns={columns}
                data={customersData}
                Row={
                    ({data}) =>
                        <Row data={data}
                             isValidWhatsAppNumber={isValidWhatsAppNumber}
                             formatWhatsAppLink={formatWhatsAppLink}/>
                }
            >

                <ButtonGroup>
                    {/*<Button
                        variant="subtle"
                        colorPalette="green"
                        onClick={() => handleExportReportType('reportsBuyCustomersMostExport', 'pdf')}
                        disabled={hasNoData}
                    >
                        <UI.Icon name="pdf"/>Exportar Pdf
                    </Button>*/}
                    <Button
                        variant="subtle"
                        colorPalette="blue"
                        onClick={handleExportCsv}
                        disabled={hasNoData}
                    >
                        <UI.Icon name="csv"/>Export CSV
                    </Button>
                </ButtonGroup>

                <Form.Interval
                    initial={initialPeriod}
                    onSelect={(p) => setPeriod(p)}
                />


                <UI.Show visible={!!validPeriod}>
                    <Button variant="ghost" size="xs" onClick={handleResetFilters}>
                        <UI.Icon name="cancel"/> Limpar filtros
                    </Button>
                </UI.Show>

            </UI.Table>

            {hasMultipleMonths && monthsList.length > 0 && !hasNoData && (
                <Tabs.Root
                    variant="enclosed"
                    value={activeTab}
                    onValueChange={(e) => setActiveTab(e.value)}
                    mt={4}
                >
                    <Tabs.List>
                        {monthsList.map(month => (
                            <Tabs.Trigger key={month} value={month} colorPalette='orange'>
                                {month}
                            </Tabs.Trigger>
                        ))}
                    </Tabs.List>

                    {monthsList.map(month => (
                        <Tabs.Content key={month} value={month}>
                            <Table.Root>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.ColumnHeader>Cliente</Table.ColumnHeader>
                                        <Table.ColumnHeader>E-mail</Table.ColumnHeader>
                                        <Table.ColumnHeader>Celular</Table.ColumnHeader>
                                        <Table.ColumnHeader textAlign="right">Total em compras</Table.ColumnHeader>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {(report.data?.months[month] || []).map((customer, idx) => (
                                        <Row key={idx} data={customer} isValidWhatsAppNumber={isValidWhatsAppNumber}
                                             formatWhatsAppLink={formatWhatsAppLink}/>
                                    ))}
                                </Table.Body>
                            </Table.Root>
                        </Tabs.Content>
                    ))}
                </Tabs.Root>
            )}

            {!report.isFetching && hasNoData && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p={8}
                    borderRadius="lg"
                    borderWidth="1px"
                    mt={4}
                >
                    <Box textAlign="center">
                        <UI.Icon
                            name='empty'
                            size="48px"
                            mb={3}
                        />
                        <Text fontSize="lg" fontWeight="medium" mb={2}>
                            Nenhum cliente encontrado
                        </Text>
                        <Text fontSize="sm" mb={4}>
                            {hasMultipleMonths
                                ? "Não há clientes com compras no período selecionado"
                                : "Não há clientes com compras no período selecionado"}
                        </Text>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleResetFilters}
                        >
                            <UI.Icon name="refresh" />
                            Limpar filtros
                        </Button>
                    </Box>
                </Box>
            )}
        </>
    )
}

const Row = ({data, isValidWhatsAppNumber, formatWhatsAppLink}) => {
    const whatsappLink = formatWhatsAppLink(data.mobile)
    const hasValidWhatsApp = isValidWhatsAppNumber(data.mobile)

    return (
        <Table.Row>
            <Table.Cell fontSize="sm">
                <Text textTransform="capitalize"
                      fontSize="xs"
                      fontWeight="bold"
                >
                    {data.customer_name}
                </Text>
            </Table.Cell>
            <Table.Cell fontSize="sm">
                {data.email ? (
                    <UI.Link href={`mailto:${data.email}`}>
                        <Text fontSize="xs" fontWeight="bold">
                            {data.email}
                        </Text>
                    </UI.Link>
                ) : (
                    <Text fontSize="xs" fontWeight="bold" color="gray.500">
                        Não informado
                    </Text>
                )}
            </Table.Cell>
            <Table.Cell fontSize="sm">
                {hasValidWhatsApp ? (
                    <UI.Link href={whatsappLink} target="_blank" rel="noopener noreferrer">
                        <Text fontSize="xs" fontWeight="bold" display="inline-flex" alignItems="center" gap={1}>
                            {data.mobile}
                            <UI.Icon name="whatsapp" color="green"/>
                        </Text>
                    </UI.Link>
                ) : (
                    <Text fontSize="xs" fontWeight="bold" color="gray.500">
                        {data.mobile || 'Não informado'}
                    </Text>
                )}
            </Table.Cell>
            <Table.Cell textAlign="right" fontWeight="bold">
                <UI.Number.Amount value={data?.total}/>
            </Table.Cell>
        </Table.Row>
    )
}